// src/pages/AdminRoster.jsx
import React, { useEffect, useMemo, useState } from "react";
import api, { API_BASE } from "../services/api";
import ImageUploaderServer from "../components/ImageUploaderServer";

const DJ_FOOTER =
  "For any third party work or brand collaborations, please contact HLT Management at: hlt-mgmt@hey-lets-travel.com";

const HLT_WHATSAPP_SHORT_LINK = "https://wa.me/message/5UT3QJZMUBKNP1";
const imgSrc = (url) => (url?.startsWith("http") ? url : `${API_BASE}${url || ""}`);

// ---------------- Readability tweak (local CSS) ----------------
const FIELD_READABLE_CSS = `
  .field.field--readable {
    background: rgba(255,255,255,0.06);
    border-color: rgba(255,255,255,0.22);
    color: #E9EEF6;                /* brighter text */
    padding: 10px 14px;            /* px-3.5 py-2.5 */
    line-height: 1.6;              /* leading-relaxed */
    font-size: 15px;               /* clearer size */
    letter-spacing: 0.2px;
    border-radius: 0.75rem;        /* rounded-xl */
    display: block;                /* ensure full-width */
    width: 100%;
    max-width: none;               /* prevent inherited caps */
  }
  .field.field--readable::placeholder {
    color: #A8B0BF;                /* clearer placeholder */
  }
  .field.field--readable:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(99,102,241,0.28); /* brand-ish focus ring */
    border-color: rgba(99,102,241,0.38);
  }
  textarea.field.field--readable {
    resize: vertical;
    scrollbar-gutter: stable;
  }
`;

// --- small helpers ---
const slugify = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

export default function AdminRoster() {
  const [category, setCategory] = useState("DJ");
  const [filterActive, setFilterActive] = useState("all"); // all | true | false
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");

  const emptyForm = {
    name: "",
    slug: "",
    imageUrl: "",
    blurb: "",
    location: "",
    genre: "",
    inspiration: "",
    youtubeUrls: [],
    instagramUrls: [],
    instagramHandle: "",
    spotifyUrl: "",
    soundcloudUrl: "",
    tiktokHandle: "",
    popularAlbumImageUrl: "",
    popularSongUrl: "",
    sortOrder: 1000,
    active: true,
    inquiry: false, // NEW (default OFF)
    isIndexable: true,
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(
        `/api/admin/roster?category=${category}&active=${filterActive}`
      );
      setItems(data || []);
    } catch (e) {
      setItems([]);
      setErr(
        e?.response?.status === 401
          ? "You are signed out. Please log in to manage the roster."
          : "Failed to load roster."
      );
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, filterActive]);

  // autoslug
  const onNameChange = (e) => {
    const name = e.target.value;
    const auto = slugify(name);
    const wasAuto = !form.slug || form.slug === slugify(form.name || "");
    setForm({ ...form, name, slug: wasAuto ? auto : form.slug });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const setList = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.value
        .split(/\n|,/)
        .map((s) => s.trim())
        .filter(Boolean),
    });

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    const mergedInquiry =
      it.inquiry !== undefined ? it.inquiry : it.showEnquiry ?? false;
    setForm({
      ...emptyForm,
      ...it,
      inquiry: !!mergedInquiry,
      youtubeUrls: it.youtubeUrls || [],
      instagramUrls: it.instagramUrls || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    setErr("");
    try {
      const payload = { ...form, category, showEnquiry: form.inquiry }; // back-compat
      if (editingId) {
        await api.put(`/api/admin/roster/${editingId}`, payload);
      } else {
        await api.post("/api/admin/roster", payload);
      }
      cancelEdit();
      await load();
    } catch (e) {
      setErr(
        e?.response?.status === 401
          ? "You are signed out. Please log in to continue."
          : "Failed to save entry."
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setErr("");
    try {
      await api.delete(`/api/admin/roster/${id}`);
      await load();
    } catch (e) {
      setErr(
        e?.response?.status === 401
          ? "You are signed out. Please log in to continue."
          : "Failed to delete entry."
      );
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.name?.toLowerCase().includes(q) ||
        it.slug?.toLowerCase().includes(q) ||
        it.location?.toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="page space-y-6">
      {/* local readability styles */}
      <style>{FIELD_READABLE_CSS}</style>

      {/* Toolbar */}
      <section className="card">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-2xl md:text-3xl mr-auto">Roster Admin</h1>

          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-300">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-xl bg-[var(--surface)] border border-white/10 px-3 py-2 text-white"
            >
              <option>DJ</option>
              <option>INFLUENCER</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-300">Status</label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="rounded-xl bg-[var(--surface)] border border-white/10 px-3 py-2 text-white"
              title="Filter by active status"
            >
              <option value="all">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="relative">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, slug, location…"
              className="w-64 rounded-xl bg-[var(--surface)] border border-white/10 px-3 py-2 pr-8 text-white placeholder:text-slate-400"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">⌘K</span>
          </div>

          <button className="btn-primary" onClick={startNew}>New</button>
        </div>
      </section>

      {/* Error */}
      {err && (
        <div className="card bg-red-500/10 border-red-500/20 text-red-200">{err}</div>
      )}

      {/* Editor + Preview */}
      <section className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="card">
          <div className="space-y-6">
            <SectionTitle title="Identity" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name">
                <input
                  value={form.name}
                  onChange={onNameChange}
                  placeholder="Artist name"
                  className="field field--readable"
                />
              </Field>
              <Field label="Slug">
                <input
                  value={form.slug}
                  onChange={set("slug")}
                  placeholder="auto-from-name or custom"
                  className="field field--readable"
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Location">
                <input
                  value={form.location}
                  onChange={set("location")}
                  placeholder="City, Country"
                  className="field field--readable"
                />
              </Field>
              <Field label="Genre (DJs)">
                <input
                  value={form.genre}
                  onChange={set("genre")}
                  placeholder="House / Techno / UKG"
                  className="field field--readable"
                />
              </Field>
              <Field label="Inspiration (DJs)">
                <input
                  value={form.inspiration}
                  onChange={set("inspiration")}
                  placeholder="Influences / vibe"
                  className="field field--readable"
                />
              </Field>
            </div>

            <SectionTitle title="Portrait" />
            <Field>
              <ImageUploaderServer
                folder={category === "DJ" ? "roster/djs" : "roster/influencers"}
                value={form.imageUrl}
                onChange={(url) => setForm({ ...form, imageUrl: url })}
              />
              {form.imageUrl && (
                <img
                  src={imgSrc(form.imageUrl)}
                  alt="preview"
                  className="mt-3 w-full max-w-xs aspect-[4/5] object-cover rounded-xl border border-white/10"
                  loading="lazy"
                />
              )}
            </Field>

            <SectionTitle title="Bio" />
            <Field
              full
              label="Bio / blurb"
              hint={
                <>
                  Management enquiry line can be auto-shown beneath the bio (toggle in Socials).
                  <span className="block mt-1 text-slate-300">{DJ_FOOTER}</span>
                </>
              }
            >
              {/* Wider & taller, with improved readability */}
              <textarea
                value={form.blurb}
                onChange={set("blurb")}
                placeholder="Short artist bio…"
                className="field field--readable min-h-[320px] md:min-h-[420px] max-w-none"
              />
            </Field>

            <SectionTitle title="Media embeds" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="YouTube URLs (one per line)">
                <textarea
                  rows={4}
                  value={(form.youtubeUrls || []).join("\n")}
                  onChange={setList("youtubeUrls")}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="field field--readable"
                />
              </Field>
              <Field label="Instagram post/reel URLs (one per line)">
                <textarea
                  rows={4}
                  value={(form.instagramUrls || []).join("\n")}
                  onChange={setList("instagramUrls")}
                  placeholder="https://www.instagram.com/p/... or /reel/..."
                  className="field field--readable"
                />
              </Field>
            </div>

            <SectionTitle title="Socials" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Instagram handle or URL">
                <input
                  value={form.instagramHandle}
                  onChange={set("instagramHandle")}
                  placeholder="@name or https://instagram.com/name"
                  className="field field--readable"
                />
              </Field>
              <Field label="TikTok handle or URL (Affiliates)">
                <input
                  value={form.tiktokHandle}
                  onChange={set("tiktokHandle")}
                  placeholder="@name or https://www.tiktok.com/@name"
                  className="field field--readable"
                />
              </Field>
              <Field label="Spotify URL">
                <input
                  value={form.spotifyUrl}
                  onChange={set("spotifyUrl")}
                  placeholder="https://open.spotify.com/..."
                  className="field field--readable"
                />
              </Field>
              <Field label="SoundCloud URL">
                <input
                  value={form.soundcloudUrl}
                  onChange={set("soundcloudUrl")}
                  placeholder="https://soundcloud.com/..."
                  className="field field--readable"
                />
              </Field>
            </div>

            {/* WhatsApp toggle */}
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <label className="inline-flex items-center gap-2 text-slate-100">
                <input
                  type="checkbox"
                  checked={!!form.inquiry}
                  onChange={(e) => setForm({ ...form, inquiry: e.target.checked })}
                />
                <span className="font-medium">
                  Show HLT Management WhatsApp contact
                </span>
                <span className="ml-2 inline-flex items-center gap-1 text-emerald-200 text-sm">
                  <WhatsAppIcon className="h-4 w-4" />
                  WhatsApp
                </span>
              </label>
              <p className="mt-2 text-sm text-slate-300">
                When enabled, a WhatsApp icon/link is shown next to socials on the public
                profile and beneath the enquiry line.
                <span className="block text-xs text-slate-400 mt-1">
                  Current link: {HLT_WHATSAPP_SHORT_LINK}
                </span>
              </p>
            </div>

            <SectionTitle title="Featured (DJs)" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Popular album image">
                {category === "DJ" ? (
                  <>
                    <ImageUploaderServer
                      folder="roster/djs/featured"
                      value={form.popularAlbumImageUrl}
                      onChange={(url) =>
                        setForm({ ...form, popularAlbumImageUrl: url })
                      }
                    />
                    {form.popularAlbumImageUrl && (
                      <img
                        src={imgSrc(form.popularAlbumImageUrl)}
                        alt="Popular release preview"
                        className="mt-3 w-full max-w-xs aspect-square object-cover rounded-xl border border-white/10"
                        loading="lazy"
                      />
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-400">Not applicable for Creators.</p>
                )}
              </Field>

              <Field label="Popular song URL (Spotify or SoundCloud)">
                <input
                  value={form.popularSongUrl}
                  onChange={set("popularSongUrl")}
                  placeholder="https://open.spotify.com/track/... or https://soundcloud.com/..."
                  className="field field--readable"
                />
              </Field>
            </div>

            <SectionTitle title="SEO" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Meta title">
                <input
                  value={form.metaTitle}
                  onChange={set("metaTitle")}
                  placeholder="Custom page title"
                  className="field field--readable"
                />
              </Field>
              <Field label="Meta description">
                <input
                  value={form.metaDescription}
                  onChange={set("metaDescription")}
                  placeholder="Short SEO description"
                  className="field field--readable"
                />
              </Field>
              <Field label="Meta keywords" full>
                <input
                  value={form.metaKeywords}
                  onChange={set("metaKeywords")}
                  placeholder="comma,separated,keywords"
                  className="field field--readable"
                />
              </Field>
            </div>

            <SectionTitle title="Settings" />
            <div className="flex flex-wrap items-center gap-6">
              <label className="inline-flex items-center gap-2 text-slate-200">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                Active
              </label>

              <label className="inline-flex items-center gap-2 text-slate-200">
                <input
                  type="checkbox"
                  checked={form.isIndexable}
                  onChange={(e) => setForm({ ...form, isIndexable: e.target.checked })}
                />
                Indexable (SEO)
              </label>

              <div className="ml-auto flex items-center gap-3">
                <label className="text-sm text-slate-300">Sort order</label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={set("sortOrder")}
                  className="w-28 rounded-xl bg-[var(--surface)] border border-white/10 px-3 py-2 text-white"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              {editingId && (
                <button type="button" onClick={cancelEdit} className="btn-secondary">
                  Cancel
                </button>
              )}
              <button
                onClick={save}
                disabled={saving}
                className="btn-primary disabled:opacity-60"
              >
                {saving ? "Saving…" : editingId ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <aside className="card sticky top-4 h-fit">
          <h3 className="text-sm font-semibold text-white mb-3">Live preview</h3>
          <div className="space-y-3">
            <div className="relative">
              {form.imageUrl ? (
                <img
                  src={imgSrc(form.imageUrl)}
                  alt="preview"
                  className="w-full aspect-[4/5] object-cover rounded-xl border border-white/10"
                />
              ) : (
                <div className="w-full aspect-[4/5] rounded-xl border border-white/10 bg-white/5 grid place-items-center text-slate-400">
                  No image
                </div>
              )}
              <span className={`badge absolute left-2 top-2 ${form.active ? "bg-green-500/20 text-green-200" : "bg-slate-500/20 text-slate-200"}`}>
                {form.active ? "Active" : "Hidden"}
              </span>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-white text-lg truncate">
                {form.name || "Artist name"}
              </div>
              <div className="text-sm text-slate-400">
                {form.location || "Location TBC"}
              </div>
              {form.genre && (
                <div className="text-sm text-slate-400">Genre: {form.genre}</div>
              )}
            </div>

            {/* Enquiry & WhatsApp hint */}
            <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3 text-sm text-slate-300">
              {form.inquiry ? (
                <>
                  <div>Enquiries enabled.</div>
                  <div className="mt-1 inline-flex items-center gap-1 text-emerald-200">
                    <WhatsAppIcon className="h-4 w-4" />
                    WhatsApp link will show.
                  </div>
                </>
              ) : (
                <div>Enquiries hidden.</div>
              )}
            </div>

            {/* Social chips preview */}
            <div className="flex flex-wrap gap-2">
              {form.instagramHandle && <Chip>Instagram</Chip>}
              {form.spotifyUrl && <Chip>Spotify</Chip>}
              {form.soundcloudUrl && <Chip>SoundCloud</Chip>}
              {form.tiktokHandle && <Chip>TikTok</Chip>}
              {form.inquiry && <Chip className="text-emerald-200 border-emerald-400/30">WhatsApp</Chip>}
              {!form.instagramHandle &&
                !form.spotifyUrl &&
                !form.soundcloudUrl &&
                !form.tiktokHandle &&
                !form.inquiry && <span className="text-xs text-slate-500">No socials yet</span>}
            </div>
          </div>
        </aside>
      </section>

      {/* List */}
      <section className="card">
        <div className="mb-3 text-sm text-slate-400">
          Showing <span className="text-slate-200">{filtered.length}</span> of{" "}
          <span className="text-slate-200">{items.length}</span>
        </div>

        {filtered.length === 0 ? (
          <div className="text-slate-300">No entries yet.</div>
        ) : (
          <ul className="divide-y divide-white/10">
            {filtered
              .slice()
              .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
              .map((it) => {
                const enquiryOn =
                  it.inquiry !== undefined ? it.inquiry : it.showEnquiry ?? false;
                const cat = (it.category || category).toLowerCase();
                return (
                  <li key={it.id} className="py-3">
                    <div className="flex items-center gap-3">
                      {/* Thumb */}
                      <div className="shrink-0">
                        {it.imageUrl ? (
                          <img
                            src={imgSrc(it.imageUrl)}
                            alt={it.name}
                            loading="lazy"
                            className="h-14 w-14 rounded-lg object-cover border border-white/10"
                          />
                        ) : (
                          <div className="h-14 w-14 rounded-lg border border-white/10 bg-white/5 grid place-items-center text-[10px] text-slate-400">
                            No img
                          </div>
                        )}
                      </div>

                      {/* Main */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-semibold text-white truncate">
                            {it.name}
                          </div>
                          <span
                            className={`badge ${it.active
                              ? "bg-green-500/20 text-green-200"
                              : "bg-slate-500/20 text-slate-200"
                              }`}
                          >
                            {it.active ? "Active" : "Hidden"}
                          </span>
                          <span
                            className={`badge ${enquiryOn
                              ? "bg-blue-500/20 text-blue-200"
                              : "bg-slate-500/20 text-slate-200"
                              }`}
                            title="HLT Management WhatsApp on profile"
                          >
                            {enquiryOn ? "Enquiries: On" : "Enquiries: Off"}
                          </span>
                          <span className="badge bg-white/10 text-slate-300">
                            {it.sortOrder ?? 0}
                          </span>
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          /talent/{cat}/{it.slug}
                        </div>
                        {it.blurb && (
                          <p className="mt-1 text-sm text-slate-300 line-clamp-2">
                            {it.blurb}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {it.slug && (
                          <a
                            className="btn-ghost"
                            href={`/talent/${cat}/${it.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            title="View public page"
                          >
                            View
                          </a>
                        )}
                        <button
                          onClick={() => startEdit(it)}
                          className="btn-secondary"
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => remove(it.id)}
                          className="btn-ghost"
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        )}
      </section>
    </div>
  );
}

/* ---------- Small presentational helpers ---------- */

function SectionTitle({ title }) {
  return (
    <div className="flex items-center gap-3 pt-1">
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      <div className="h-px flex-1 bg-white/10" />
    </div>
  );
}

function Field({ label, hint, children, full }) {
  return (
    <div className={full ? "sm:col-span-2 col-span-full" : ""}>
      {label && (
        <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">
          {label}
        </label>
      )}
      {children}
      {hint && <div className="mt-2 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

function Chip({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-1 text-xs ${className} border-white/15 text-slate-200 bg-white/5`}
    >
      {children}
    </span>
  );
}

function WhatsAppIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M20.52 3.48A11.93 11.93 0 0 0 12.01 0C5.39 0 .03 5.36.03 11.98c0 2.11.55 4.17 1.6 5.99L0 24l6.22-1.62a11.97 11.97 0 0 0 5.78 1.49h.01c6.62 0 11.99-5.36 11.99-11.98a11.9 11.9 0 0 0-3.48-8.41zM12.01 21.8h-.01a9.82 9.82 0 0 1-4.99-1.36l-.36-.22-3.69.96.99-3.6-.24-.37a9.83 9.83 0 0 1-1.51-5.21c0-5.43 4.42-9.85 9.85-9.85 2.63 0 5.11 1.02 6.97 2.88a9.78 9.78 0 0 1 2.88 6.97c0 5.42-4.42 9.8-9.89 9.8zm5.6-7.35c-.31-.16-1.82-.9-2.1-1a.63.63 0 0 0-.61.08c-.17.12-.71.65-.87.87a.56.56 0 0 1-.6.2 9.82 9.82 0 0 1-3.06-1.89 11.53 11.53 0 0 1-2.13-2.64.63.63 0 0 1 .18-.85c.09-.08.2-.21.3-.31.1-.1.18-.2.27-.32a.6.6 0 0 0 .05-.64c-.06-.16-.48-1.17-.66-1.6-.18-.44-.37-.38-.54-.39h-.46a.9.9 0 0 0-.65.31 2.73 2.73 0 0 0-.84 2.03c0 1.2.87 2.36 1 2.53.12.16 1.73 2.66 4.2 3.74.59.25 1.05.4 1.41.51.59.19 1.13.16 1.55.1.47-.07 1.46-.59 1.67-1.16.21-.57.21-1.06.14-1.16a.7.7 0 0 0-.33-.32z" />
    </svg>
  );
}

/* Tailwind shortcut pre-existing:
.field = "w-full rounded-xl bg-[var(--surface)] border border-white/10 px-3 py-2 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
*/
